import { Store } from "bernie-core";
import { ClientFactories } from "bernie-plugin-mobx";
import { ClientLogger } from "bernie-client";
import { AdvertisingStore } from "bernie-plugin-ads-loader-script";

import { ShortlistStore } from "./ShortlistStore";
import { UIPageStore } from "./UIPageStore";
import { HotelsStore } from "./HotelsStore";
import { ThemeSelectorStore } from "./ThemeSelectorStore";
import { BexApiService } from "services/BexApiService";
import { ClientStaticMapStore } from "./staticMap/ClientStaticMapStore";
import { DXStore } from "./DXStore";
import { Logger, NOOP_LOGGER } from "bernie-logger";
import { CookiesStore } from "stores/CookiesStore";
import { ClientMarkdownStore } from "./markdownHandler/ClientMarkdownStore";
import { ClientWizardStateStore } from "./wizard/ClientWizardStateStore";
import { ContextInput } from "__generated__/api";
import { ClientCompositionStore } from "src/stores/CompositionStore";
import { DestinationSuggestionsStore } from "stores/DestinationSuggestionsStore";
import { ClientFlexViewModelStore } from "stores/flexViewModel/ClientFlexViewModelStore";
import { TemplateStore } from "stores/TemplateStore";
import { ClientChatbotStore } from "./chatbotStore/ClientChatbotStore";
import { ClientMabStore } from "stores/mabStore/ClientMabStore";
import { PackagePillStore } from "./PackagePillStore";
import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";
import { FlexModuleModelStore } from "./FlexModuleModelStore";
import { HotwireConfigStore } from "./HotwireConfigStore";
import { PropertyFiltersStore } from "./PropertyFiltersStore";
import { FlightsStore } from "stores/FlightsStore";
import ClientSherpaStore from "./Sherpa/ClientSherpa";
import { TravelAgentSearchWizardConfigStore } from "stores/TravelAgentSearchWizardConfigStore";
import { UriContextStore } from "./UriContextStore/UriContextStore";
import { ExperienceTemplateStore } from "stores/ExperienceTemplateStore";
import { DataDogStore } from "./DataDogStore";

interface WindowWithState extends Window {
  __STATE__: {
    context: ExtendedContextStore;
  };
}

export const buildBexApiServiceWithHeaders = (bexApiContext: ContextInput, logger: Logger) => {
  return new BexApiService("", logger, {
    "Device-User-Agent-ID": bexApiContext.identity?.duaid,
    "x-page-id": ((window as unknown) as WindowWithState)?.__STATE__?.context?.pageId || null,
  });
};

export const stores: ClientFactories = {
  flexViewModel: (): Store =>
    new ClientFlexViewModelStore({}, ClientLogger.getLoggerWithIdentifier("blossom.store.viewmodel")),
  compositionStore: () =>
    new ClientCompositionStore({}, ClientLogger.getLoggerWithIdentifier("blossom.store.composition")),
  templateStore: (): Store => new TemplateStore({}, ClientLogger.getLoggerWithIdentifier("blossom.store.template")),
  experienceTemplateStore: (): Store =>
    new ExperienceTemplateStore({}, ClientLogger.getLoggerWithIdentifier("blossom.store.experienceTemplate")),
  flexModuleModelStore: () =>
    new FlexModuleModelStore({}, ClientLogger.getLoggerWithIdentifier("blossom.store.flexModuleModel")),
  shortlist: (): Store => new ShortlistStore({}, ClientLogger.getLoggerWithIdentifier("blossom.store.shortlist")),
  uiPage: (): Store => new UIPageStore({}, ClientLogger.getLoggerWithIdentifier("blossom.store.uiPage")),
  markdown: (): Store => new ClientMarkdownStore({}, ClientLogger.getLoggerWithIdentifier("blossom.store.markdown")),
  cookies: (): Store => new CookiesStore({}, ClientLogger.getLoggerWithIdentifier("blossom.store.cookies")),
  hotels: (): Store => {
    const logger = ClientLogger.getLoggerWithIdentifier("blossom.store.hotels");

    return new HotelsStore({}, logger);
  },
  staticMap: (): Store => new ClientStaticMapStore({}, NOOP_LOGGER),
  dx: (): Store => new DXStore({}, ClientLogger.getLoggerWithIdentifier("blossom.store.dx")),
  advertising: (): AdvertisingStore =>
    new AdvertisingStore({}, ClientLogger.getLoggerWithIdentifier("blossom.store.advertising")),
  destinationSuggestions: (): Store =>
    new DestinationSuggestionsStore({}, ClientLogger.getLoggerWithIdentifier("blossom.store.destinationSuggestions")),
  wizardState: (): Store =>
    new ClientWizardStateStore({}, ClientLogger.getLoggerWithIdentifier("blossom.store.wizardState")),
  propertyFilters: (): Store => {
    const logger = ClientLogger.getLoggerWithIdentifier("blossom.store.propertyFilters");

    return new PropertyFiltersStore({}, logger);
  },
  themeSelectorStore: () =>
    new ThemeSelectorStore({}, ClientLogger.getLoggerWithIdentifier("blossom.store.themeSelectorStore")),
  chatbot: (): Store => new ClientChatbotStore({}, ClientLogger.getLoggerWithIdentifier("blossom.store.chatbotStore")),
  mab: (): Store => new ClientMabStore({}, ClientLogger.getLoggerWithIdentifier("blossom.store.mabStore")),
  packagePillStore: () =>
    new PackagePillStore({}, ClientLogger.getLoggerWithIdentifier("blossom.store.packagePillStore")),
  flightsStore: () => new FlightsStore({}, ClientLogger.getLoggerWithIdentifier("blossom.store.flightsStore")),
  hotwireConfig: (): Store =>
    new HotwireConfigStore({}, ClientLogger.getLoggerWithIdentifier("blossom.store.hotwireConfigStore")),

  sherpa: (): Store => new ClientSherpaStore({}, ClientLogger.getLoggerWithIdentifier("blossom.store.sherpaStore")),
  travelAgentSearchWizardConfigStore: ({ bexApiContext }): Store => {
    const logger = ClientLogger.getLoggerWithIdentifier("blossom.store.travelAgentSearchWizardConfigStore");
    const bexApiService = buildBexApiServiceWithHeaders(bexApiContext, logger);

    return new TravelAgentSearchWizardConfigStore({}, logger, bexApiService);
  },
  uriContext: () => new UriContextStore(),
  datadog: (): Store => new DataDogStore({}),
};
